import React from "react";

/**
 * O que a tela de rampas mostra quando o cliente ainda não pode operar.
 *
 * ── Por que é um portão, e não um aviso ──
 *
 * Saldo e botões ao lado de "seu cadastro está em análise" convidam o cliente a
 * tentar uma operação que vai ser recusada no servidor — e a recusa chega tarde,
 * depois de ele preencher tudo. Aqui o estado do cadastro OCUPA o lugar da
 * operação, e só sai quando ele pode operar de verdade.
 *
 * ── Os quatro estados, e por que são quatro ──
 *
 * `carregando`  — não decide nada. Mostrar o convite ao cadastro antes de saber
 *                 se já existe cadastro faria o cliente preencher de novo o que
 *                 já preencheu.
 * `sem cadastro`— o começo do fluxo de todo cliente liberado para rampas.
 * `em análise`  — nada a fazer além de esperar; dizer isso evita o reenvio.
 * `recusado`    — o único em que há ação, e por isso é o único que lista itens.
 *
 * O motivo da recusa aparece COMO O PROVEDOR ESCREVEU. Reescrever daria ao cliente
 * uma explicação que o suporte do provedor não reconhece — e é lá que o documento
 * recusado se resolve.
 */

const APPROVED_STATUSES = ["approved", "active"];

export function isRampApproved(profile) {
  if (!profile?.hasProfile) return false;
  return APPROVED_STATUSES.includes(String(profile.status || "").toLowerCase());
}

export default function RampOnboardingGate({ profile, onStartOnboarding }) {
  // `null` = ainda carregando. Não decide nada: ver o comentário do arquivo.
  if (profile === null) {
    return (
      <section className="card">
        <p style={{ margin: 0, color: "var(--muted)" }}>Verificando seu cadastro...</p>
      </section>
    );
  }

  if (!profile.hasProfile) {
    return (
      <section className="card">
        <h3 style={{ marginTop: 0 }}>Cadastro necessário</h3>
        <p style={{ color: "var(--muted)" }}>
          Para operar rampas, precisamos dos documentos da sua empresa. É uma vez só
          — depois disso a tela de operações fica liberada.
        </p>
        <button type="button" className="btnSaveAndContinue" onClick={onStartOnboarding}>
          Preencher cadastro
        </button>
      </section>
    );
  }

  const rejected = Array.isArray(profile.rejected) ? profile.rejected : [];

  if (rejected.length > 0) {
    return (
      <section className="card">
        <h3 style={{ marginTop: 0 }}>Cadastro recusado</h3>
        <p style={{ color: "var(--muted)" }}>
          Estes itens precisam ser reenviados. O envio complementar reabre a análise —
          você não recomeça do zero.
        </p>
        <ul style={{ paddingLeft: 18 }}>
          {rejected.map((issue, index) => (
            <li key={`${issue.name}-${index}`} style={{ marginBottom: 8 }}>
              <strong>{issue.name}</strong>
              {/* A frase do provedor, sem tradução nossa. */}
              {issue.description ? <div>{issue.description}</div> : null}
              {Array.isArray(issue.codes) && issue.codes.length > 0 ? (
                <div style={{ color: "var(--muted)", fontSize: 12 }}>
                  {issue.codes.join(", ")}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {/*
          A recusa NÃO reabre o formulário de cadastro: o cadastro já existe, e reenviá-lo
          criaria um segundo registro no provedor (que o servidor recusa com 409). O que
          falta são os documentos dos itens acima, e isso passa pelo contato na Higher
          enquanto o envio de arquivos pelo app não existir.
        */}
        <p style={{ color: "var(--muted)", fontSize: 13 }}>
          Envie os itens acima ao seu contato na Higher. O envio complementar reabre a
          análise — você não recomeça do zero.
        </p>
      </section>
    );
  }

  const pending = Array.isArray(profile.pending) ? profile.pending : [];

  return (
    <section className="card">
      <h3 style={{ marginTop: 0 }}>Cadastro em análise</h3>
      <p style={{ color: "var(--muted)" }}>
        Recebemos seus documentos. Assim que a análise terminar, a tela de operações
        aparece aqui — não é preciso reenviar nada.
      </p>
      {pending.length > 0 ? (
        <p style={{ color: "var(--muted)", fontSize: 13 }}>
          Em verificação: {pending.join(", ")}
        </p>
      ) : null}
    </section>
  );
}
